import { employeeVisibleComments } from 'tio-common/utils/tuition-assistance/comments';
import type { CommentExtension as CommentModel } from 'tio-data/data/resources/comment.ext';

export {
  employeeVisibleComments,
  EMPLOYEE_VISIBLE_COMMENT_TAGS,
} from 'tio-common/utils/tuition-assistance/comments';

/**
 * Counts employee-visible comments absent from the client's seen-ID set, excluding the current person's own comments.
 * IDs avoid comparing author/viewer clocks; ownership uses person ID because display names can collide.
 * Missing seen IDs count everything as unread, and a missing person ID excludes no authors.
 */
export function unreadCommentCount(
  comments: CommentModel[] | undefined | null,
  seenIds: ReadonlySet<string> | null,
  currentPersonId?: string | null
): number {
  return employeeVisibleComments(comments).filter((comment) => {
    if (currentPersonId && comment.person?.id === currentPersonId) {
      return false;
    }
    // No id means nothing could have recorded it as seen, so it counts. (In
    // practice `employeeVisibleComments` has already dropped unsaved records, which
    // are the only ones without one.)
    return comment.id ? !seenIds?.has(comment.id) : true;
  }).length;
}

/** Ids to persist when the drawer opens: everything the employee could see at that
 * moment, own comments included, so the stored set is a faithful record of what was
 * on screen rather than a filtered view of it. */
export function visibleCommentIds(comments: CommentModel[] | undefined | null): string[] {
  return employeeVisibleComments(comments)
    .map((comment) => comment.id)
    .filter((id): id is string => Boolean(id));
}

/** localStorage key holding the comment ids this employee has seen for one
 * application. Scoped per application so reading one request's thread does not
 * clear the badge on another. */
export function commentsSeenStorageKey(applicationId: string): string {
  return `tas-manage-comments-seen:${applicationId}`;
}

/** Parses the stored id list, tolerating anything that isn't the shape we wrote —
 * a corrupt or hand-edited value degrades to "nothing seen", which shows a badge
 * rather than hiding one. */
export function parseSeenCommentIds(raw: string | null): ReadonlySet<string> | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return null;
    }
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return null;
  }
}
